/**
 * SubmitPage.tsx — 提交新 Prompt
 * 表单校验后 POST /api/prompts，成功跳转提示并给出详情页链接。
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { createPrompt } from '../lib/api';
import { seedCategories } from '../data/seed';
import { trackEvent } from '../lib/analytics';

interface FormState {
  title: string;
  category: string;
  content: string;
  author: string;
  coverUrl: string;
}

const initialForm: FormState = {
  title: '',
  category: 'writing',
  content: '',
  author: '',
  coverUrl: '',
};

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100';

export default function SubmitPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);

  function updateField<K extends keyof FormState>(key: K, value: string): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setStatus('error');
      setMessage('标题与 Prompt 内容为必填项');
      return;
    }
    setStatus('submitting');
    try {
      const prompt = await createPrompt({
        title: form.title.trim(),
        content: form.content.trim(),
        category: form.category,
        author: form.author.trim() || '匿名',
        coverUrl: form.coverUrl.trim() || undefined,
      });
      setCreatedId(prompt.id);
      setMessage('提交成功！感谢你的分享。');
      setStatus('success');
      trackEvent('submit_prompt', { category: form.category });
      setForm(initialForm);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '提交失败，请稍后重试');
      setStatus('error');
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">提交新 Prompt</h1>
        <p className="mt-1 text-sm text-slate-500">
          分享你的优质 Prompt，帮助更多创作者。提交后将写入 D1 数据库。
        </p>
      </div>

      {status === 'success' && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          <p className="font-medium">{message}</p>
          {createdId && (
            <Link
              to={`/prompt/${createdId}`}
              className="mt-2 inline-block font-semibold text-green-800 underline"
            >
              查看刚提交的 Prompt →
            </Link>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-slate-700">
            标题 *
          </label>
          <input
            id="title"
            type="text"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            maxLength={100}
            placeholder="例如：公众号爆款标题生成器"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-slate-700">
            分类 *
          </label>
          <select
            id="category"
            value={form.category}
            onChange={(e) => updateField('category', e.target.value)}
            className={inputClass}
          >
            {seedCategories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-slate-700">
            Prompt 内容 *
          </label>
          <textarea
            id="content"
            value={form.content}
            onChange={(e) => updateField('content', e.target.value)}
            rows={8}
            maxLength={20000}
            placeholder="粘贴完整的 Prompt 文本…"
            className={`${inputClass} resize-y`}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="author" className="mb-1.5 block text-sm font-medium text-slate-700">
              作者昵称
            </label>
            <input
              id="author"
              type="text"
              value={form.author}
              onChange={(e) => updateField('author', e.target.value)}
              maxLength={50}
              placeholder="留空显示「匿名」"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="coverUrl" className="mb-1.5 block text-sm font-medium text-slate-700">
              封面图 URL（可选）
            </label>
            <input
              id="coverUrl"
              type="url"
              value={form.coverUrl}
              onChange={(e) => updateField('coverUrl', e.target.value)}
              placeholder="https://…"
              className={inputClass}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中…' : '提交 Prompt'}
        </button>
      </form>
    </div>
  );
}
